import { useCallback } from 'react';

import { useCustomerPreferences } from 'services/customerPreferences';
import {
  PqScaleType,
  formatPq,
  pqScaleLabel,
  pqScaleMax,
  pqScaleMin,
  transformPq,
} from 'utils/pqTransform';

export interface PqTransform {
  /** The configured scale type */
  scale: PqScaleType;
  /** Transform a raw 7-pt PQ value to the configured scale */
  transform: (pqValue: number) => number;
  /** Transform and format a PQ value for display */
  format: (pqValue: number | string, decimals?: number) => string;
  /** Display label (e.g., "PQ" or "PQ (9-pt)") */
  label: string;
  /** Max value on the configured scale */
  max: number;
  /** Min value on the configured scale */
  min: number;
}

/**
 * Hook that provides PQ transformation functions based on the
 * current producer's configured PQ scale preference.
 */
export default function usePqTransform(): PqTransform {
  const customerPreferences = useCustomerPreferences();
  const scale: PqScaleType = customerPreferences?.perceivedQualityScale?.ninePoint ? '9pt' : '7pt';

  const transform = useCallback(
    (pqValue: number) => transformPq(pqValue, scale),
    [scale],
  );

  const format = useCallback(
    (pqValue: number | string, decimals = 2) =>
      formatPq(pqValue, scale, decimals),
    [scale],
  );

  return {
    scale,
    transform,
    format,
    label: pqScaleLabel(scale),
    max: pqScaleMax(scale),
    min: pqScaleMin(scale),
  };
}
